import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZapIcon, XIcon, StarIcon, BookOpenIcon, BrainIcon, WandIcon, SunIcon, UsersIcon, FilmIcon, PaletteIcon, SearchIcon, TargetIcon, TrendingUpIcon, CalendarIcon, SettingsIcon, VideoIcon, RocketIcon, BarChartIcon, ShieldCheckIcon, AlertTriangleIcon, CheckIcon, ChevronDownIcon, LockIcon, DownloadIcon, ClockIcon, GiftIcon } from 'lucide-react';
import { RecentPurchaseToast } from '../components/RecentPurchaseToast';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
const META_PIXEL_ID = '874982625170304';
const BASE_CART_URL = 'https://rutinasimple.com//cart/52416824115481:1';
const CART_URLS: Record<string, string> = {
  '': BASE_CART_URL,
  // Singles
  '0': 'https://rutinasimple.com//cart/52416868155673:1',
  '1': 'https://rutinasimple.com//cart/52425825583385:1',
  '2': 'https://rutinasimple.com//cart/52425828598041:1',
  '3': 'https://rutinasimple.com//cart/52425834922265:1',
  // Pairs
  '0,1': 'https://rutinasimple.com//cart/52416901611801:1',
  '0,2': 'https://rutinasimple.com//cart/52417124499737:1',
  '0,3': 'https://rutinasimple.com//cart/52417074200857:1',
  '1,2': 'https://rutinasimple.com//cart/52417095729433:1',
  '1,3': 'https://rutinasimple.com//cart/52417250361625:1',
  '2,3': 'https://rutinasimple.com//cart/52416976224537:1',
  // Triples
  '0,1,2': 'https://rutinasimple.com//cart/52416921141529:1',
  '0,1,3': 'https://rutinasimple.com//cart/52425839247641:1',
  '0,2,3': 'https://rutinasimple.com//cart/52425864741145:1',
  '1,2,3': 'https://rutinasimple.com//cart/52425846358297:1',
  // All four
  '0,1,2,3': 'https://rutinasimple.com//cart/52416946602265:1'
};
function getCartUrl(selected: Set<number>): string {
  const key = Array.from(selected).sort().join(',');
  return CART_URLS[key] || BASE_CART_URL;
}
const IMAGES = {
  book: 'https://cdn.magicpatterns.com/uploads/seepzgHZDhCYcA8He93CYd/mk-01.png',
  prompts: 'https://cdn.magicpatterns.com/uploads/fFLupSoezNKupQ3Sr2Nqte/B-01.png',
  adLibrary: 'https://cdn.magicpatterns.com/uploads/hwfwPBKc75jDa9LbtsUdsu/B-02.png',
  plan30: 'https://cdn.magicpatterns.com/uploads/csFsXq2bZt5fmdH7UbocWr/B-03.png'
};
/* ─── Reusable Animated Wrapper ─── */
function FadeIn({
  children,
  className = '',
  delay = 0,
  id,
  style
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  id?: string;
  style?: React.CSSProperties;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-20px'
  }} transition={{
    duration: 0.3,
    delay: Math.min(delay, 0.15),
    ease: 'easeOut'
  }} className={className} id={id} style={{
    ...style,
    contentVisibility: 'auto' as any
  }} data-id="element-187">
      {children}
    </motion.div>;
}
/* ─── CTA Button ─── */
function V2CTA({
  text = 'QUIERO EL PROTOCOLO COMPLETO — $11.99',
  href,
  onBeforeCheckout
}: {
  text?: string;
  href?: string;
  onBeforeCheckout?: () => boolean;
}) {
  const handleClick = () => {
    if (href) {
      if (onBeforeCheckout && !onBeforeCheckout()) return;
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    const el = document.getElementById('oferta-v2');
    if (el) el.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <motion.button whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} onClick={handleClick} className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFD700] text-black font-bold text-sm md:text-base tracking-wide uppercase rounded cursor-pointer transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.35)]" data-id="element-188">
      <ZapIcon className="w-5 h-5" data-id="element-189" />
      {text}
    </motion.button>;
}
/* ─── Exit Intent Upsell Popup ─── */
const ALL_BUMPS_URL = 'https://rutinasimple.com//cart/52425891053849:1';
const BUMPS_TOTAL = 39.96;
const BUNDLE_DISCOUNT = 0.3;
const DISCOUNTED_BUMPS = +(BUMPS_TOTAL * (1 - BUNDLE_DISCOUNT)).toFixed(2);
const BUNDLE_TOTAL = +(11.99 + DISCOUNTED_BUMPS).toFixed(2);
function ExitPopup({
  onClose,
  onAccept,
  onDecline
}: {
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return <AnimatePresence data-id="element-190">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      backgroundColor: 'rgba(0,0,0,0.85)'
    }} onClick={onClose} data-id="element-191">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#FFD700]/30 rounded-lg overflow-hidden max-h-[90vh] overflow-y-auto" data-id="element-192">
          {/* Header Banner */}
          <div className="bg-[#FFD700] px-4 py-3 text-center" data-id="element-193">
            <p className="text-black text-xs font-bold tracking-widest uppercase animate-pulse" data-id="element-194">
              OFERTA EXCLUSIVA — SOLO AHORA
            </p>
          </div>

          <div className="p-5 md:p-7" data-id="element-195">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-12 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" data-id="element-196">
              <XIcon className="w-4 h-4 text-white" data-id="element-197" />
            </button>

            {/* Title */}
            <h2 className="font-montserrat text-xl md:text-2xl font-extrabold text-white text-center mb-1 leading-tight" data-id="element-198">
              ESPERA — NO TE VAYAS SIN ESTO
            </h2>
            <p className="text-gray-400 text-sm text-center mb-5" data-id="element-199">
              Tienes una oportunidad que no se va a repetir.
            </p>

            {/* Discount Badge */}
            <div className="flex justify-center mb-4" data-id="element-200">
              <span className="inline-block px-4 py-2 bg-red-600 text-white text-sm font-bold rounded animate-pulse" data-id="element-201">
                30% OFF EN TODOS LOS EXTRAS
              </span>
            </div>

            {/* Main copy */}
            <p className="text-gray-300 text-sm text-center leading-relaxed mb-2" data-id="element-202">
              Llévate el{' '}
              <span className="text-white font-semibold" data-id="element-203">
                protocolo completo + los 4 extras
              </span>{' '}
              que multiplican tus resultados con IA — todo el sistema que
              necesitas para crear anuncios cinematográficos que dominan Meta
              Ads.
            </p>
            <p className="text-gray-400 text-xs text-center mb-5" data-id="element-204">
              El bundle incluye el producto principal, 3 bonos y los 4 order
              bumps con un 30% de descuento.
            </p>

            {/* What's included */}
            <div className="bg-[#111111] border border-[#FFD700]/10 rounded p-4 mb-5" data-id="element-205">
              <p className="text-[#FFD700] text-xs font-bold tracking-wider uppercase mb-3" data-id="element-206">
                TODO LO QUE INCLUYE:
              </p>
              <div className="space-y-2" data-id="element-207">
                {['Nano-Creative Bible: El Protocolo de 9 Cifras', 'La Bóveda de Prompts de Oro (NANO)', 'Checklist "Ad-Library Mastery"', 'Plan de Acción "30 Días a tu Primer Ad"', 'El Clon de Agencias: Spy Scripts — Modela anuncios ganadores', 'Puntos de Dolor Visuales — Imágenes que detienen el scroll', 'El Atlas de Entornos de Autoridad — Creatives premium con IA', 'El Código de las Emociones Magnéticas — Anuncios imposibles de ignorar'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-208">
                    <CheckIcon className="w-4 h-4 text-[#FFD700] mt-0.5 flex-shrink-0" data-id="element-209" />
                    <p className="text-gray-300 text-xs" data-id="element-210">{item}</p>
                  </div>)}
              </div>
            </div>

            {/* Pricing */}
            <div className="text-center mb-5" data-id="element-211">
              <p className="text-gray-500 text-xs mb-1" data-id="element-212">
                Valor total:{' '}
                <span className="line-through" data-id="element-213">
                  ${(11.99 + BUMPS_TOTAL).toFixed(2)}
                </span>
              </p>
              <p className="font-montserrat text-3xl md:text-4xl font-extrabold text-[#FFD700]" style={{
              textShadow: '0 0 30px rgba(255,215,0,0.2)'
            }} data-id="element-214">
                ${BUNDLE_TOTAL}
              </p>
              <p className="text-gray-400 text-xs mt-1" data-id="element-215">
                Ahorras ${(BUMPS_TOTAL * BUNDLE_DISCOUNT).toFixed(2)} con esta
                oferta
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3" data-id="element-216">
              <motion.button whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.97
            }} onClick={onAccept} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#FFD700] text-black font-bold text-sm tracking-wide uppercase rounded cursor-pointer transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.35)]" data-id="element-217">
                <ZapIcon className="w-5 h-5" data-id="element-218" />
                QUIERO EL BUNDLE COMPLETO — ${BUNDLE_TOTAL}
              </motion.button>

              <button onClick={onDecline} className="w-full text-center text-gray-500 text-xs hover:text-gray-400 transition-colors cursor-pointer py-2 underline" data-id="element-219">
                No gracias, solo quiero el producto principal por $11.99
              </button>
            </div>

            {/* Trust */}
            <div className="flex items-center justify-center gap-3 mt-4 text-gray-600 text-[10px]" data-id="element-220">
              <span className="flex items-center gap-1" data-id="element-221">
                <ShieldCheckIcon className="w-3 h-3" data-id="element-222" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-223">
                <LockIcon className="w-3 h-3" data-id="element-224" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-225">
                <DownloadIcon className="w-3 h-3" data-id="element-226" /> Acceso inmediato
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
/* ─── Section Divider ─── */
function Divider() {
  return <div className="flex items-center justify-center py-4" data-id="element-227">
      <div className="h-px w-12 bg-[#FFD700]/20" data-id="element-228" />
      <div className="mx-3 w-1.5 h-1.5 rotate-45 bg-[#FFD700]/40" data-id="element-229" />
      <div className="h-px w-12 bg-[#FFD700]/20" data-id="element-230" />
    </div>;
}
/* ═══════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════ */
export function LpdNano1() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedBumps, setSelectedBumps] = useState<Set<number>>(new Set());
  const [showExitPopup, setShowExitPopup] = useState(false);
  const BASE_PRICE = 11.99;
  const BUMP_PRICE = 9.99;
  const totalPrice = BASE_PRICE + selectedBumps.size * BUMP_PRICE;
  const toggleBump = (index: number) => {
    setSelectedBumps(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  useEffect(() => {
    if (typeof window === 'undefined' || window.fbq) return;
    const n: any = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    window.fbq = n;
    window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }, []);
  return <div className="w-full min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden font-['Inter']" data-id="element-231">
      {/* ═══ SECTION 1 — HERO ═══ */}
      <section className="px-4 pt-16 pb-10 md:pt-24 md:pb-16" data-id="element-232">
        <div className="max-w-3xl mx-auto text-center" data-id="element-233">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="font-montserrat text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-[#FFD700] mb-4 md:mb-6" data-id="element-234">
            EN 2026, EL QUE NO DOMINE IA PARA ANUNCIOS VA A DESAPARECER.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-xl md:text-2xl font-semibold text-white mb-6 md:mb-8" data-id="element-235">
            Y los que lo dominen primero se van a quedar con todo.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 md:mb-10 text-left md:text-center space-y-4" data-id="element-236">
            <p data-id="element-237">
              Mientras tú sigues pagando $3,000 por sesiones de fotos que nadie
              mira, un chavo de 22 años en su cuarto está creando anuncios
              cinematográficos con IA que facturan más que tu agencia completa.
            </p>
            <p data-id="element-238">
              La diferencia entre él y tú no es talento. No es dinero. No es
              experiencia.
            </p>
            <p className="text-[#FFD700] font-semibold text-lg md:text-xl" data-id="element-239">
              Es un protocolo.
            </p>
            <p data-id="element-240">
              Y hoy lo tienes en tus manos por menos de lo que cuesta un
              almuerzo.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.9
        }} className="mb-8 md:mb-10" data-id="element-241">
            <img src="https://cdn.magicpatterns.com/uploads/diFbSb3p2yX5Z5hgngZajA/HERO_IMAGE_(Seccion_1).png" alt="Nano-Creative Labs — Hero" className="w-full max-w-2xl mx-auto rounded" fetchPriority="high" decoding="async" data-id="element-242" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 1.1
        }} data-id="element-243">
            <V2CTA data-id="element-244" />
          </motion.div>
        </div>
      </section>

      <Divider data-id="element-245" />

      {/* ═══ SECTION 2 — PROBLEM ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-246">
        <div className="max-w-3xl mx-auto border-l-2 border-[#1a6b3a] pl-6 md:pl-8" data-id="element-247">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] mb-6 md:mb-8" data-id="element-248">
            DEJA DE MENTIRTE. TU CONTENIDO NO ESTÁ FUNCIONANDO.
          </h2>

          <div className="space-y-3 mb-8" data-id="element-249">
            {['Pagas diseñadores que entregan en 5 días lo que la IA hace en 5 minutos', 'Tus anuncios se ven iguales a los de todos los demás', 'Gastaste $500 en stock photos que parecen de 2014', 'Tu CTR está por el suelo y no sabes por qué', 'Ves marcas nuevas explotando y no entiendes cómo lo hacen'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-250">
                <XIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" data-id="element-251" />
                <p className="text-gray-300 text-sm md:text-base" data-id="element-252">{item}</p>
              </div>)}
          </div>

          <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-253">
            <span className="text-white font-semibold" data-id="element-254">
              ¿Quieres la verdad?
            </span>{' '}
            Esas marcas no tienen más presupuesto que tú. Tienen mejor
            tecnología. Y la están usando mientras tú lo piensas.
          </p>
        </div>
      </FadeIn>

      <Divider data-id="element-255" />

      {/* ═══ SECTION 3 — OPPORTUNITY ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" style={{
      background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1a12 100%)'
    } as any} data-id="element-256">
        <div className="max-w-3xl mx-auto" data-id="element-257">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] mb-6 md:mb-8 text-center" data-id="element-258">
            EXISTE UNA VENTANA DE 6 MESES. DESPUÉS SE CIERRA.
          </h2>

          <p className="text-gray-400 text-center mb-6 text-sm md:text-base" data-id="element-259">
            Ahora mismo estamos en el punto exacto donde:
          </p>

          <div className="space-y-3 mb-8" data-id="element-260">
            {['La tecnología ya es capaz de crear anuncios de nivel Hollywood', 'El 97% del mercado todavía no sabe cómo usarla', 'Los costos de producción cayeron de $5,000 a $0', 'Meta está priorizando contenido visual de alta calidad en su algoritmo'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-261">
                <StarIcon className="w-5 h-5 text-[#FFD700] mt-0.5 flex-shrink-0" fill="#FFD700" data-id="element-262" />
                <p className="text-gray-200 text-sm md:text-base" data-id="element-263">{item}</p>
              </div>)}
          </div>

          <div className="text-gray-300 text-base md:text-lg leading-relaxed space-y-4 mb-8" data-id="element-264">
            <p data-id="element-265">
              Esto es exactamente lo que pasó con Facebook Ads en 2015, con
              TikTok en 2020 y con Reels en 2022.
            </p>
            <p data-id="element-266">
              Los que llegaron primero construyeron imperios. Los que llegaron
              tarde pagaron 10x más por las mismas migajas.
            </p>
            <p className="text-[#FFD700] font-semibold" data-id="element-267">
              ¿De qué lado quieres estar?
            </p>
          </div>

          <div className="text-center" data-id="element-268">
            <V2CTA text="QUIERO ENTRAR AHORA — $11.99" data-id="element-269" />
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-270" />

      {/* ═══ SECTION 4 — SOLUTION ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-271">
        <div className="max-w-4xl mx-auto" data-id="element-272">
          <div className="text-center mb-6" data-id="element-273">
            <span className="inline-block px-4 py-1.5 bg-[#1a6b3a] text-black text-xs font-bold tracking-widest uppercase rounded-sm mb-4" data-id="element-274">
              NUEVO 2026
            </span>
            <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] mb-3" data-id="element-275">
              NANO-CREATIVE BIBLE: EL PROTOCOLO DE 9 CIFRAS
            </h2>
            <p className="text-gray-300 text-base md:text-lg" data-id="element-276">
              La guía definitiva para dominar Meta Ads con IA en 2026
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-277">
            <div className="flex-1 space-y-4 text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-278">
              <p data-id="element-279">
                Esto no es un curso. No es un PDF genérico. No es un "tutorial
                de prompts" reciclado de YouTube.
              </p>
              <p data-id="element-280">
                Es un{' '}
                <span className="text-white font-semibold" data-id="element-281">
                  sistema de producción completo de 10 capítulos
                </span>{' '}
                que te lleva de cero a crear anuncios cinematográficos con IA en
                15 minutos.
              </p>
              <p data-id="element-282">
                Sin cámaras de $5,000. Sin modelos. Sin agencia. Sin experiencia
                en diseño.
              </p>
              <p className="text-[#FFD700] font-semibold" data-id="element-283">
                Solo tú, tu laptop y este protocolo.
              </p>
            </div>
            <div className="flex-shrink-0 relative" data-id="element-284">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-285">
                <div className="w-56 h-56 rounded-full bg-[#FFD700]/5 blur-[60px]" data-id="element-286" />
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/pBz5r6o1ekFKvKkRpoqFAV/MOCKUP_PRODUCTO_PRINCIPAL_(Seccion_4).png" alt="Nano-Creative Bible — The Banana Guide" loading="lazy" decoding="async" className="relative z-10 w-56 md:w-72 drop-shadow-2xl" data-id="element-287" />
            </div>
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-288" />

      {/* ═══ SECTION 5 — WHAT'S INSIDE ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-289">
        <div className="max-w-4xl mx-auto" data-id="element-290">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-8 md:mb-10" data-id="element-291">
            LOS 10 CAPÍTULOS QUE VAN A CAMBIAR TU NEGOCIO
          </h2>

          <div className="grid md:grid-cols-2 gap-4" data-id="element-292">
            {[{
            icon: BrainIcon,
            num: 1,
            title: 'La Psicología del Scroll-Stop',
            desc: 'Por qué el 95% de los anuncios mueren en 0.3 segundos y cómo hacer que el tuyo sobreviva'
          }, {
            icon: WandIcon,
            num: 2,
            title: 'Nano Banana como Motor de Renderizado',
            desc: 'La herramienta que está reemplazando estudios fotográficos completos'
          }, {
            icon: TargetIcon,
            num: 3,
            title: 'Ingeniería de Prompts Nivel Maestro',
            desc: 'El lenguaje exacto que separa renders amateur de piezas de $10,000'
          }, {
            icon: SunIcon,
            num: 4,
            title: 'La Luz — Pro vs Amateur',
            desc: 'Iluminación cinematográfica sin un solo softbox físico'
          }, {
            icon: UsersIcon,
            num: 5,
            title: 'Consistencia de Personajes con IA',
            desc: 'Mismo rostro. Misma marca. Infinitas variaciones.'
          }, {
            icon: FilmIcon,
            num: 6,
            title: 'Movimiento con Veo 3 — Del Caos al Cine',
            desc: 'Imágenes estáticas convertidas en videos que hipnotizan'
          }, {
            icon: PaletteIcon,
            num: 7,
            title: 'Psicología del Color y Conversión',
            desc: 'Los colores que venden vs. los que repelen. Con datos.'
          }, {
            icon: SearchIcon,
            num: 8,
            title: 'Modelado de Ganadores / Spying Strategy',
            desc: 'Encuentra y clona lo que ya factura millones'
          }, {
            icon: BookOpenIcon,
            num: 9,
            title: 'La Estructura del Anuncio Perfecto',
            desc: 'Hook → Problema → Solución → CTA. La anatomía que convierte.'
          }, {
            icon: TrendingUpIcon,
            num: 10,
            title: 'Escalado Exponencial',
            desc: 'Del primer render al sistema que produce 50 creativos por semana'
          }].map((ch, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: i * 0.03
          }} className="flex gap-4 bg-[#111111] border border-[#FFD700]/10 rounded p-4" data-id="element-293">
                <div className="w-10 h-10 rounded-full border border-[#FFD700]/30 flex items-center justify-center flex-shrink-0" data-id="element-294">
                  <ch.icon className="w-4 h-4 text-[#FFD700]" data-id="element-295" />
                </div>
                <div data-id="element-296">
                  <p className="text-[#FFD700] text-xs font-bold tracking-wider mb-1" data-id="element-297">
                    CAP {ch.num}
                  </p>
                  <h3 className="text-white font-semibold text-sm mb-1" data-id="element-298">
                    {ch.title}
                  </h3>
                  <p className="text-gray-400 text-xs leading-relaxed" data-id="element-299">
                    {ch.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-300" />

      {/* ═══ SECTION 6 — 30-DAY PLAN ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" style={{
      background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1a12 50%, #0a0a0a 100%)'
    } as any} data-id="element-301">
        <div className="max-w-3xl mx-auto" data-id="element-302">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-8 md:mb-10" data-id="element-303">
            DE CERO A MÁQUINA DE ANUNCIOS EN 30 DÍAS
          </h2>

          <div className="space-y-4 mb-8" data-id="element-304">
            {[{
            week: 'SEMANA 1',
            title: 'SETUP Y PRIMEROS RENDERS',
            desc: 'Configuras todo, aprendes el sistema y generas tus primeras imágenes de nivel agencia',
            icon: SettingsIcon
          }, {
            week: 'SEMANA 2',
            title: 'ANIMACIÓN Y VIDEO',
            desc: 'Dominas Veo 3, creas tus primeros reels cinematográficos desde renders estáticos',
            icon: VideoIcon
          }, {
            week: 'SEMANA 3',
            title: 'ESTRATEGIA Y LANZAMIENTO',
            desc: 'Construyes tu primera campaña real con creativos que detienen el scroll',
            icon: RocketIcon
          }, {
            week: 'SEMANA 4',
            title: 'OPTIMIZACIÓN Y ESCALA',
            desc: 'Analizas datos, iteras ganadores y construyes un sistema que se autoproduce',
            icon: BarChartIcon
          }].map((w, i) => <div key={i} className="flex gap-4 bg-[#111111] border border-[#1a6b3a]/30 rounded p-4 md:p-5" data-id="element-305">
                <div className="w-10 h-10 rounded-full bg-[#1a6b3a]/20 flex items-center justify-center flex-shrink-0" data-id="element-306">
                  <w.icon className="w-5 h-5 text-[#FFD700]" data-id="element-307" />
                </div>
                <div data-id="element-308">
                  <p className="text-[#FFD700] text-xs font-bold tracking-wider mb-1" data-id="element-309">
                    {w.week} — {w.title}
                  </p>
                  <p className="text-gray-300 text-sm" data-id="element-310">{w.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="text-center" data-id="element-311">
            <V2CTA text="EMPEZAR MI TRANSFORMACIÓN — $11.99" data-id="element-312" />
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-313" />

      {/* ═══ SECTION 7 — BONUSES ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-314">
        <div className="max-w-4xl mx-auto" data-id="element-315">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-2" data-id="element-316">
            PERO ESO NO ES TODO.
          </h2>
          <p className="text-[#1a6b3a] font-semibold text-center text-sm md:text-base tracking-wider uppercase mb-8 md:mb-10" data-id="element-317">
            MIRA LO QUE RECIBES GRATIS CON TU COMPRA.
          </p>

          {/* Bonus 1 */}
          <div className="bg-[#111111] border border-[#FFD700]/15 rounded p-5 md:p-8 mb-5" data-id="element-318">
            <div className="flex flex-col md:flex-row gap-5 md:gap-8" data-id="element-319">
              <img src="https://cdn.magicpatterns.com/uploads/t5ygNHkKsnidpukFNWwCe8/MOCKUP_BONO_1_—_LA_BOVEDA_DE_PROMPTS_DE_ORO_(Seccion_7).png" alt="Bóveda de Prompts de Oro" loading="lazy" decoding="async" className="w-40 md:w-52 object-contain mx-auto md:mx-0 flex-shrink-0" data-id="element-320" />
              <div data-id="element-321">
                <div className="flex items-center gap-2 mb-2" data-id="element-322">
                  <GiftIcon className="w-5 h-5 text-[#FFD700]" data-id="element-323" />
                  <span className="text-[#FFD700] text-xs font-bold tracking-wider" data-id="element-324">
                    BONO 1
                  </span>
                </div>
                <h3 className="font-montserrat text-lg md:text-xl font-bold text-white mb-1" data-id="element-325">
                  LA BÓVEDA DE PROMPTS DE ORO (NANO)
                </h3>
                <p className="text-sm text-gray-400 mb-3" data-id="element-326">
                  Valor Real: <span className="line-through" data-id="element-327">$67 USD</span> —{' '}
                  <span className="text-[#1a6b3a] font-bold" data-id="element-328">GRATIS</span> con
                  tu compra
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-3" data-id="element-329">
                  Deja de adivinar qué escribir en Nano Banana. Esta bóveda
                  contiene los prompts exactos de ingeniería visual optimizados
                  para consistencia de marca, iluminación profesional y renders
                  que parecen sacados de una campaña de $50,000.
                </p>
                <div className="space-y-1.5" data-id="element-330">
                  {['Prompts organizados por categoría: producto, lifestyle, retrato, entorno', 'Cada prompt con variables editables para adaptar a cualquier nicho', 'Sistema de consistencia visual integrado', 'Actualizaciones incluidas'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-331">
                      <CheckIcon className="w-4 h-4 text-[#1a6b3a] mt-0.5 flex-shrink-0" data-id="element-332" />
                      <p className="text-gray-400 text-xs" data-id="element-333">{item}</p>
                    </div>)}
                </div>
                <p className="text-[#FFD700] text-sm font-semibold mt-3" data-id="element-334">
                  Copia, pega, renderiza. Así de simple.
                </p>
              </div>
            </div>
          </div>

          {/* Bonus 2 */}
          <div className="bg-[#111111] border border-[#FFD700]/15 rounded p-5 md:p-8 mb-5" data-id="element-335">
            <div className="flex flex-col md:flex-row gap-5 md:gap-8" data-id="element-336">
              <img src="https://cdn.magicpatterns.com/uploads/wW9mtWCzzdYEV9EiLWEq4v/MOCKUP_BONO_2_—_CHECKLIST_AD-LIBRARY_MASTERY_(Seccion_7).png" alt="Checklist Ad-Library Mastery" loading="lazy" decoding="async" className="w-40 md:w-52 object-contain mx-auto md:mx-0 flex-shrink-0" data-id="element-337" />
              <div data-id="element-338">
                <div className="flex items-center gap-2 mb-2" data-id="element-339">
                  <GiftIcon className="w-5 h-5 text-[#FFD700]" data-id="element-340" />
                  <span className="text-[#FFD700] text-xs font-bold tracking-wider" data-id="element-341">
                    BONO 2
                  </span>
                </div>
                <h3 className="font-montserrat text-lg md:text-xl font-bold text-white mb-1" data-id="element-342">
                  CHECKLIST "AD-LIBRARY MASTERY"
                </h3>
                <p className="text-sm text-gray-400 mb-3" data-id="element-343">
                  Valor Real: <span className="line-through" data-id="element-344">$47 USD</span> —{' '}
                  <span className="text-[#1a6b3a] font-bold" data-id="element-345">GRATIS</span> con
                  tu compra
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-3" data-id="element-346">
                  El Meta Ad Library es una mina de oro. El problema es que el
                  99% de la gente lo abre, scrollea sin rumbo y lo cierra sin
                  sacar nada útil. Esta checklist te da el protocolo exacto para
                  espiar y modelar los anuncios que más facturan en tu nicho hoy
                  mismo.
                </p>
                <div className="space-y-1.5" data-id="element-347">
                  {['Filtros de búsqueda avanzada para encontrar ganadores rápido', 'Checklist de deconstrucción: qué mirar en cada anuncio ganador', 'Plantilla de extracción: cómo convertir lo que ves en un prompt ejecutable', 'Criterios de validación: cómo saber si un anuncio realmente está facturando o solo tiene likes'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-348">
                      <CheckIcon className="w-4 h-4 text-[#1a6b3a] mt-0.5 flex-shrink-0" data-id="element-349" />
                      <p className="text-gray-400 text-xs" data-id="element-350">{item}</p>
                    </div>)}
                </div>
                <p className="text-[#FFD700] text-sm font-semibold mt-3" data-id="element-351">
                  Deja de crear desde cero. Modela lo que ya funciona.
                </p>
              </div>
            </div>
          </div>

          {/* Bonus 3 */}
          <div className="bg-[#111111] border border-[#FFD700]/15 rounded p-5 md:p-8" data-id="element-352">
            <div className="flex flex-col md:flex-row gap-5 md:gap-8" data-id="element-353">
              <img src="https://cdn.magicpatterns.com/uploads/njQPjicjhiTr4n6sgbHnnc/MOCKUP_BONO_3_—_PLAN_7_DIAS_A_TU_PRIMER_AD_(Seccion_7).png" alt="Plan de Acción 7 Días" loading="lazy" decoding="async" className="w-40 md:w-52 object-contain mx-auto md:mx-0 flex-shrink-0" data-id="element-354" />
              <div data-id="element-355">
                <div className="flex items-center gap-2 mb-2" data-id="element-356">
                  <GiftIcon className="w-5 h-5 text-[#FFD700]" data-id="element-357" />
                  <span className="text-[#FFD700] text-xs font-bold tracking-wider" data-id="element-358">
                    BONO 3
                  </span>
                </div>
                <h3 className="font-montserrat text-lg md:text-xl font-bold text-white mb-1" data-id="element-359">
                  PLAN DE ACCIÓN "30 DÍAS A TU PRIMER AD"
                </h3>
                <p className="text-sm text-gray-400 mb-3" data-id="element-360">
                  Valor Real: <span className="line-through" data-id="element-361">$37 USD</span> —{' '}
                  <span className="text-[#1a6b3a] font-bold" data-id="element-362">GRATIS</span> con
                  tu compra
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-3" data-id="element-363">
                  Nada de "léelo y después vemos". Este plan de acción diario te
                  toma de la mano durante 30 días consecutivos para que al final
                  del mes tengas tu primer anuncio profesional creado con IA,
                  listo para lanzar y vender.
                </p>
                <div className="space-y-1.5" data-id="element-364">
                  {['Día 1: Setup y configuración', 'Día 2: Tu primer render profesional', 'Día 3: Variaciones y testing visual', 'Día 4: Copy + Creative = Anuncio completo', 'Día 5: Optimización y refinamiento', 'Día 6: Estructura de campaña en Meta', 'Día 7: Lanzamiento y monitoreo'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-365">
                      <CheckIcon className="w-4 h-4 text-[#1a6b3a] mt-0.5 flex-shrink-0" data-id="element-366" />
                      <p className="text-gray-400 text-xs" data-id="element-367">{item}</p>
                    </div>)}
                </div>
                <p className="text-[#FFD700] text-sm font-semibold mt-3" data-id="element-368">
                  En 30 días vas a tener lo que la mayoría no logra en meses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-369" />

      {/* ═══ SECTION 8 — VALUE STACK ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" id="oferta-v2" data-id="element-370">
        <div className="max-w-3xl mx-auto" data-id="element-371">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-6" data-id="element-372">
            TODO LO QUE RECIBES HOY
          </h2>

          <img src="https://cdn.magicpatterns.com/uploads/skA2fFHGRjQnmDBMDEgu3Q/VALUE_STACK_VISUAL_(Seccion_8).png" alt="Value Stack — Producto principal + 3 bonos" loading="lazy" decoding="async" className="w-full max-w-2xl mx-auto mb-8 rounded" data-id="element-373" />

          <div className="border border-[#FFD700]/20 rounded overflow-hidden mb-6" data-id="element-374">
            <table className="w-full" data-id="element-375">
              <tbody data-id="element-376">
                {[{
                icon: BookOpenIcon,
                name: 'Nano-Creative Bible: El Protocolo de 9 Cifras',
                value: '$97'
              }, {
                icon: GiftIcon,
                name: 'La Bóveda de Prompts de Oro (NANO)',
                value: '$67'
              }, {
                icon: GiftIcon,
                name: 'Checklist "Ad-Library Mastery"',
                value: '$47'
              }, {
                icon: GiftIcon,
                name: 'Plan de Acción "30 Días a tu Primer Ad"',
                value: '$37'
              }].map((item, i) => <tr key={i} className="border-b border-[#1a1a1a] last:border-b-0" data-id="element-377">
                    <td className="p-3 md:p-4" data-id="element-378">
                      <div className="flex items-center gap-3" data-id="element-379">
                        <item.icon className="w-4 h-4 text-[#FFD700] flex-shrink-0" data-id="element-380" />
                        <span className="text-gray-200 text-xs md:text-sm" data-id="element-381">
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 md:p-4 text-right" data-id="element-382">
                      <span className="text-gray-400 text-sm font-semibold" data-id="element-383">
                        {item.value}
                      </span>
                    </td>
                  </tr>)}
                <tr className="bg-[#111111]" data-id="element-384">
                  <td className="p-3 md:p-4" data-id="element-385">
                    <span className="text-white font-bold text-sm" data-id="element-386">
                      VALOR TOTAL
                    </span>
                  </td>
                  <td className="p-3 md:p-4 text-right" data-id="element-387">
                    <span className="text-gray-500 font-bold text-lg line-through" data-id="element-388">
                      $248
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-center border border-[#FFD700]/30 rounded p-6 md:p-10 bg-[#111111] relative overflow-hidden mb-6" data-id="element-389">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-[#FFD700]/5 blur-[80px] pointer-events-none" data-id="element-390" />
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-391">
              <ZapIcon className="w-5 h-5 text-[#FFD700]" data-id="element-392" />
              <p className="text-[#FFD700] text-sm tracking-widest uppercase font-bold" data-id="element-393">
                Tu Precio Hoy
              </p>
            </div>
            <p className="font-montserrat text-5xl md:text-7xl font-extrabold text-white mb-2" style={{
            textShadow: '0 0 40px rgba(255,215,0,0.2)'
          }} data-id="element-394">
              ${totalPrice.toFixed(2)}
            </p>
            <p className="text-gray-400 text-sm mb-6" data-id="element-395">
              {selectedBumps.size > 0 ? `Producto principal + ${selectedBumps.size} extra${selectedBumps.size > 1 ? 's' : ''}. Selecciona abajo.` : 'Eso es menos del 5% del valor real. Y lo obtienes todo. Ahora. Instantáneo.'}
            </p>
            <V2CTA text={`QUIERO EL PROTOCOLO COMPLETO + BONOS — ${totalPrice.toFixed(2)}`} href={getCartUrl(selectedBumps)} onBeforeCheckout={() => {
            if (selectedBumps.size === 0) {
              setShowExitPopup(true);
              return false;
            }
            return true;
          }} data-id="element-396" />
          </div>

          {/* ── ORDER BUMPS ── */}
          <div className="space-y-4 mb-6" data-id="element-397">
            {[{
            index: 0,
            color: '#3b82f6',
            colorLight: 'rgba(59,130,246,0.1)',
            colorBorder: 'rgba(59,130,246,0.3)',
            colorBorderLight: 'rgba(59,130,246,0.2)',
            originalPrice: '$27',
            image: 'https://cdn.magicpatterns.com/uploads/rvEmb4ha3Jjj3GX2iPp425/bump-1.png',
            alt: 'El Clon de Agencias: Spy Scripts',
            title: 'EL CLON DE AGENCIAS: SPY SCRIPTS',
            hook: 'Descubre los guiones y estructuras ocultas que las agencias usan para crear anuncios que venden... y clónalos en minutos con IA.',
            desc: 'La mayoría intenta crear anuncios desde cero y por eso sus creatives se ven débiles, genéricos o improvisados. Este recurso te entrega una colección de scripts, patrones y estructuras diseñadas para modelar anuncios ganadores y convertirlos en piezas listas para usar con IA.',
            items: ['Scripts de anuncios listos para modelar', 'Frameworks de apertura, desarrollo y cierre', 'Estructuras para anuncios de alto CTR', 'Guías para adaptar scripts ganadores a tu nicho'],
            close: 'Si quieres dejar de improvisar y empezar a construir anuncios con lógica de agencia, añade esto ahora.'
          }, {
            index: 1,
            color: '#ef4444',
            colorLight: 'rgba(239,68,68,0.1)',
            colorBorder: 'rgba(239,68,68,0.3)',
            colorBorderLight: 'rgba(239,68,68,0.2)',
            originalPrice: '$36',
            image: 'https://cdn.magicpatterns.com/uploads/eZahjvoJpSivgqYiEAGjvY/bumpt-2.png',
            alt: 'Puntos de Dolor Visuales',
            title: 'PUNTOS DE DOLOR VISUALES',
            hook: 'Aprende a convertir dolores invisibles en imágenes que detienen el scroll y hacen que tu audiencia se sienta vista al instante.',
            desc: 'La mayoría de los anuncios muestran productos. Los anuncios que convierten muestran primero el dolor. Este recurso te revela cómo transformar frustraciones, objeciones y emociones del mercado en escenas visuales que conectan antes de que el usuario lea una sola palabra.',
            items: ['Biblioteca de ángulos visuales basados en dolor', 'Ejemplos de escenas que comunican frustración, urgencia y deseo', 'Ideas para representar objeciones visualmente', 'Guía para crear anuncios que golpean emocionalmente antes del copy'],
            close: 'Si tus anuncios se ven bonitos pero no provocan reacción, este extra puede cambiarlo todo.'
          }, {
            index: 2,
            color: '#eab308',
            colorLight: 'rgba(234,179,8,0.1)',
            colorBorder: 'rgba(234,179,8,0.3)',
            colorBorderLight: 'rgba(234,179,8,0.2)',
            originalPrice: '$29',
            image: 'https://cdn.magicpatterns.com/uploads/6wCTgZ9EnVeSABy91yGHdm/bumpt-3.png',
            alt: 'El Atlas de Entornos de Autoridad',
            title: 'EL ATLAS DE ENTORNOS DE AUTORIDAD',
            hook: 'Haz que tus anuncios se vean más premium, más creíbles y más caros simplemente cambiando el entorno visual correcto.',
            desc: 'Muchas veces el problema no es el producto, ni el copy, ni el diseño... es el contexto visual. Este atlas te muestra qué escenarios, locaciones y ambientes generan percepción de valor, status y confianza dentro de un anuncio.',
            items: ['Entornos visuales que transmiten autoridad inmediata', 'Referencias de fondos, espacios y setups premium', 'Ideas para elevar la percepción del producto con IA', 'Guía para elegir contextos visuales según tipo de anuncio'],
            close: 'Si quieres que tus creatives se sientan premium sin gastar en producción real, agrega esto ahora.'
          }, {
            index: 3,
            color: '#f97316',
            colorLight: 'rgba(249,115,22,0.1)',
            colorBorder: 'rgba(249,115,22,0.3)',
            colorBorderLight: 'rgba(249,115,22,0.2)',
            originalPrice: '$26',
            image: 'https://cdn.magicpatterns.com/uploads/dAaL2FnRVpV7sQdQJnS1wZ/bumpt_4.png',
            alt: 'El Código de las Emociones Magnéticas',
            title: 'EL CÓDIGO DE LAS EMOCIONES MAGNÉTICAS',
            hook: 'Descifra qué emociones hacen que un anuncio atrape, conecte y venda... y aprende a diseñarlas visualmente con intención.',
            desc: 'La mayoría de los anuncios fracasa porque se ve bien... pero no hace sentir nada. Este recurso te enseña cómo construir creativos que transmiten tensión, deseo, urgencia, alivio, poder, curiosidad o aspiración desde la primera impresión.',
            items: ['Mapa de emociones de alto impacto para creatives', 'Guía para elegir la emoción correcta según el ángulo del anuncio', 'Ejemplos de aplicación visual por tipo de campaña', 'Framework para diseñar piezas que conectan a nivel emocional'],
            close: 'Si quieres que tus anuncios no solo se vean bien, sino que también se sientan imposibles de ignorar, añade esto ahora.'
          }].map(bump => {
            const isSelected = selectedBumps.has(bump.index);
            const activeColor = isSelected ? '#FFD700' : bump.color;
            return <div key={bump.index} onClick={() => toggleBump(bump.index)} className="border rounded overflow-hidden cursor-pointer transition-all duration-300 bg-[#111111]" style={{
              borderColor: isSelected ? '#FFD700' : bump.colorBorder,
              boxShadow: isSelected ? '0 0 20px rgba(255,215,0,0.1)' : 'none'
            }} data-id="element-398">
                  <div className="px-4 py-2 border-b flex items-center justify-between" style={{
                backgroundColor: isSelected ? 'rgba(255,215,0,0.1)' : bump.colorLight,
                borderBottomColor: isSelected ? 'rgba(255,215,0,0.2)' : bump.colorBorderLight
              }} data-id="element-399">
                    <div className="flex items-center gap-2" data-id="element-400">
                      <div className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all" style={{
                    borderColor: activeColor,
                    backgroundColor: isSelected ? '#FFD700' : 'transparent'
                  }} data-id="element-401">
                        {isSelected && <CheckIcon className="w-3 h-3 text-black" data-id="element-402" />}
                      </div>
                      <p className="text-xs font-bold tracking-wider uppercase animate-pulse" style={{
                    color: activeColor
                  }} data-id="element-403">
                        ALTAMENTE RECOMENDADO
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-404">
                      <span className="text-gray-500 text-xs line-through" data-id="element-405">
                        {bump.originalPrice}
                      </span>
                      <span className="text-sm font-bold" style={{
                    color: activeColor
                  }} data-id="element-406">
                        $9.99
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-5 p-5" data-id="element-407">
                    <img src={bump.image} alt={bump.alt} loading="lazy" decoding="async" className="w-36 md:w-44 object-contain mx-auto md:mx-0 flex-shrink-0" data-id="element-408" />
                    <div data-id="element-409">
                      <h3 className="font-montserrat text-lg font-bold text-white mb-1" data-id="element-410">
                        {bump.title}
                      </h3>
                      <p className="text-sm font-semibold mb-3" style={{
                    color: activeColor
                  }} data-id="element-411">
                        {bump.hook}
                      </p>
                      <p className="text-gray-300 text-sm leading-relaxed mb-3" data-id="element-412">
                        {bump.desc}
                      </p>
                      <div className="space-y-1.5 mb-3" data-id="element-413">
                        {bump.items.map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-414">
                            <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{
                        color: activeColor
                      }} data-id="element-415" />
                            <p className="text-gray-400 text-xs" data-id="element-416">{item}</p>
                          </div>)}
                      </div>
                      <p className="text-gray-300 text-xs italic" data-id="element-417">
                        {bump.close}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {/* Dynamic total + CTA */}
          <div className="text-center border border-[#FFD700]/30 rounded p-6 md:p-8 bg-[#111111] relative overflow-hidden" data-id="element-418">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-[#FFD700]/5 blur-[80px] pointer-events-none" data-id="element-419" />
            {selectedBumps.size > 0 && <p className="text-gray-400 text-xs mb-2" data-id="element-420">
                Producto principal + {selectedBumps.size} extra
                {selectedBumps.size > 1 ? 's' : ''} seleccionado
                {selectedBumps.size > 1 ? 's' : ''}
              </p>}
            <div className="flex items-center justify-center gap-2 mb-1" data-id="element-421">
              <ZapIcon className="w-5 h-5 text-[#FFD700]" data-id="element-422" />
              <p className="text-[#FFD700] text-sm tracking-widest uppercase font-bold" data-id="element-423">
                Tu Total Hoy
              </p>
            </div>
            <p className="font-montserrat text-4xl md:text-6xl font-extrabold text-white mb-2" style={{
            textShadow: '0 0 40px rgba(255,215,0,0.2)'
          }} data-id="element-424">
              ${totalPrice.toFixed(2)}
            </p>
            <V2CTA text={`QUIERO TODO — ${totalPrice.toFixed(2)}`} href={getCartUrl(selectedBumps)} onBeforeCheckout={() => {
            if (selectedBumps.size === 0) {
              setShowExitPopup(true);
              return false;
            }
            return true;
          }} data-id="element-425" />
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-426" />

      {/* ═══ SECTION 9 — CONTRAST ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-427">
        <div className="max-w-3xl mx-auto" data-id="element-428">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-6" data-id="element-429">
            HAGAMOS CUENTAS RÁPIDAS
          </h2>

          <img src="https://cdn.magicpatterns.com/uploads/sGrzQzdTX5YZqD61jhYeVd/CONTRASTE_BRUTAL_VISUAL_(Seccion_9).png" alt="Contraste — Sin protocolo vs Con protocolo" loading="lazy" decoding="async" className="w-full max-w-2xl mx-auto mb-8 rounded" data-id="element-430" />

          <div className="overflow-x-auto" data-id="element-431">
            <table className="w-full min-w-[500px] border border-[#1a1a1a] rounded overflow-hidden text-sm" data-id="element-432">
              <thead data-id="element-433">
                <tr data-id="element-434">
                  <th className="p-3 md:p-4 text-left bg-red-900/20 text-red-400 font-bold text-xs tracking-wider uppercase" data-id="element-435">
                    Sin el Protocolo
                  </th>
                  <th className="p-3 md:p-4 text-left bg-[#1a6b3a]/20 text-[#FFD700] font-bold text-xs tracking-wider uppercase" data-id="element-436">
                    Con el Protocolo
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-437">
                {[['Agencia: $3,000 - $5,000/mes', '$0/mes'], ['Sesión de fotos: $500 - $2,000', '$0'], ['Stock premium: $29 - $199/mes', '$0'], ['Editor de video: $1,000 - $3,000', '$0'], ['Tiempo: 2-3 semanas', '15 minutos'], ['Resultado: genérico', 'Cinematográfico'], ['Inversión anual: $15,000+', '$11.99 una vez']].map(([without, withP], i) => <tr key={i} className="border-t border-[#1a1a1a]" data-id="element-438">
                    <td className="p-3 md:p-4 text-gray-400" data-id="element-439">{without}</td>
                    <td className="p-3 md:p-4 text-[#FFD700] font-semibold" data-id="element-440">
                      {withP}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-441" />

      {/* ═══ SECTION 10 — GUARANTEE ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" style={{
      background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1a12 50%, #0a0a0a 100%)'
    } as any} data-id="element-442">
        <div className="max-w-3xl mx-auto" data-id="element-443">
          <div className="border-2 border-[#1a6b3a]/40 rounded p-6 md:p-10 text-center bg-[#111111] relative overflow-hidden" data-id="element-444">
            <img src="https://cdn.magicpatterns.com/uploads/7niWBBTcEN438WzMEVdd4T/GARANTIA_VISUAL_(Seccion_10).png" alt="Garantía 7 días" loading="lazy" decoding="async" className="w-32 md:w-40 mx-auto mb-4" data-id="element-445" />
            <h2 className="font-montserrat text-2xl md:text-3xl font-extrabold text-[#FFD700] mb-4" data-id="element-446">
              GARANTÍA TOTAL DE 7 DÍAS — CERO RIESGO
            </h2>
            <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-3" data-id="element-447">
              <p data-id="element-448">
                Implementa el protocolo. Genera tus primeros renders. Lanza tu
                primer anuncio.
              </p>
              <p data-id="element-449">
                Si en 7 días no sientes que esto vale al menos{' '}
                <span className="text-white font-semibold" data-id="element-450">
                  100 veces lo que pagaste
                </span>
                , te devolvemos cada centavo. Sin preguntas. Sin dramas. Sin
                formularios de 47 campos.
              </p>
              <p data-id="element-451">Un click y tu dinero está de vuelta.</p>
              <p className="text-[#FFD700] font-semibold" data-id="element-452">
                El riesgo es completamente nuestro. La oportunidad es
                completamente tuya.
              </p>
            </div>
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-453" />

      {/* ═══ SECTION 11 — URGENCY ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-454">
        <div className="max-w-3xl mx-auto text-center" data-id="element-455">
          <AlertTriangleIcon className="w-10 h-10 text-[#FFD700] mx-auto mb-4" data-id="element-456" />
          <h2 className="font-montserrat text-2xl md:text-3xl font-extrabold text-[#FFD700] mb-6" data-id="element-457">
            ESTE PRECIO NO VA A DURAR.
          </h2>
          <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-3 text-left md:text-center" data-id="element-458">
            <p data-id="element-459">Estamos en fase de validación.</p>
            <p data-id="element-460">
              <span className="text-white font-semibold" data-id="element-461">$11.99</span> es el
              precio de lanzamiento. Cuando cerremos esta ventana, el precio
              sube a $47 y después a $97.
            </p>
            <p data-id="element-462">
              El stack vale $248. Lo estás obteniendo por el 4% de su valor
              real.
            </p>
            <p data-id="element-463">
              Necesitamos{' '}
              <span className="text-[#FFD700] font-semibold" data-id="element-464">
                100 compradores
              </span>{' '}
              que implementen, generen resultados y nos dejen testimonios
              reales.
            </p>
            <p className="text-white font-semibold" data-id="element-465">Tú eres uno de esos 100.</p>
            <p className="text-[#FFD700] font-semibold" data-id="element-466">
              Cuando se llenen... se acabó el precio.
            </p>
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-467" />

      {/* ═══ SECTION 12 — FAQ ═══ */}
      <FadeIn className="px-4 py-10 md:py-14" data-id="element-468">
        <div className="max-w-3xl mx-auto" data-id="element-469">
          <h2 className="font-montserrat text-2xl md:text-4xl font-extrabold text-[#FFD700] text-center mb-8" data-id="element-470">
            PREGUNTAS FRECUENTES
          </h2>

          <div className="space-y-3" data-id="element-471">
            {[{
            q: '¿Necesito experiencia en diseño?',
            a: 'No. El protocolo funciona para principiantes absolutos desde el día 1.'
          }, {
            q: '¿Qué herramientas necesito?',
            a: 'Una laptop o celular con acceso a Nano Banana (gratuito) y opcionalmente Veo 3 para video.'
          }, {
            q: '¿Funciona para mi nicho?',
            a: 'Si vendes algo online, sí. Ecommerce, servicios, SaaS, coaching, salud, real estate y más.'
          }, {
            q: '¿Es un curso?',
            a: 'No. Es un sistema de implementación. Lees, ejecutas, produces. 15 minutos = primer render.'
          }, {
            q: '¿Qué pasa si no me funciona?',
            a: 'Garantía Hotmart de 7 días. Te devolvemos cada centavo.'
          }, {
            q: '¿Por qué tan barato?',
            a: 'Fase de validación. Necesitamos resultados reales. Este precio no se repite.'
          }].map((faq, i) => <div key={i} className="border border-[#1a1a1a] bg-[#111111] rounded overflow-hidden" data-id="element-472">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer" data-id="element-473">
                  <div className="flex items-center gap-3 pr-4" data-id="element-474">
                    <StarIcon className="w-4 h-4 text-[#FFD700] flex-shrink-0" fill="#FFD700" data-id="element-475" />
                    <span className="text-white font-medium text-sm md:text-base" data-id="element-476">
                      {faq.q}
                    </span>
                  </div>
                  <motion.div animate={{
                rotate: openFaq === i ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-477">
                    <ChevronDownIcon className="w-5 h-5 text-[#FFD700] flex-shrink-0" data-id="element-478" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-479">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-480">
                      <div className="px-4 md:px-5 pb-4 md:pb-5 pl-11 md:pl-12" data-id="element-481">
                        <p className="text-gray-400 text-sm" data-id="element-482">{faq.a}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </FadeIn>

      <Divider data-id="element-483" />

      {/* ═══ SECTION 13 — FINAL CTA ═══ */}
      <FadeIn className="px-4 py-12 md:py-20" data-id="element-484">
        <div className="max-w-3xl mx-auto text-center" data-id="element-485">
          <img src="https://cdn.magicpatterns.com/uploads/jeBzZQk7HCqMzCaD6P9cwF/CTA_FINAL_CLOSING_HERO_(Seccion_13).png" alt="La oportunidad — CTA Final" loading="lazy" decoding="async" className="w-full max-w-2xl mx-auto mb-8 rounded" data-id="element-486" />

          <h2 className="font-montserrat text-xl md:text-3xl font-extrabold text-white mb-3 leading-tight" data-id="element-487">
            LA PREGUNTA NO ES SI LA IA VA A REEMPLAZAR TU FORMA DE HACER
            ANUNCIOS.
          </h2>
          <h2 className="font-montserrat text-xl md:text-3xl font-extrabold text-[#FFD700] mb-8 leading-tight" data-id="element-488">
            LA PREGUNTA ES SI VAS A SER DE LOS QUE LA USAN O DE LOS QUE LA
            SUFREN.
          </h2>

          <p className="text-gray-300 text-sm md:text-base mb-2" data-id="element-489">
            1 protocolo + 3 bonos. $248 en valor real. Lo que agencias cobran
            miles por enseñar.
          </p>
          <p className="font-montserrat text-3xl md:text-5xl font-extrabold text-[#FFD700] mb-8" style={{
          textShadow: '0 0 30px rgba(255,215,0,0.2)'
        }} data-id="element-490">
            HOY: $11.99
          </p>

          <V2CTA text="QUIERO EL PROTOCOLO COMPLETO + BONOS — $11.99" data-id="element-491" />

          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-gray-500 text-xs" data-id="element-492">
            <span className="flex items-center gap-1" data-id="element-493">
              <CheckIcon className="w-3 h-3 text-[#1a6b3a]" data-id="element-494" /> Acceso inmediato
            </span>
            <span className="flex items-center gap-1" data-id="element-495">
              <ShieldCheckIcon className="w-3 h-3 text-[#1a6b3a]" data-id="element-496" /> Garantía 7
              días
            </span>
            <span className="flex items-center gap-1" data-id="element-497">
              <DownloadIcon className="w-3 h-3 text-[#1a6b3a]" data-id="element-498" /> Descarga
              instantánea
            </span>
            <span className="flex items-center gap-1" data-id="element-499">
              <ClockIcon className="w-3 h-3 text-[#1a6b3a]" data-id="element-500" /> Precio de
              lanzamiento
            </span>
          </div>
        </div>
      </FadeIn>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-[#1a1a1a] py-6 px-4 text-center" data-id="element-501">
        <p className="text-gray-600 text-xs leading-relaxed max-w-2xl mx-auto" data-id="element-502">
          © 2026 Nano-Creative Labs. Todos los derechos reservados. Este
          producto es un material educativo digital. Los resultados pueden
          variar según la implementación individual.
        </p>
      </footer>

      <RecentPurchaseToast data-id="element-503" />

      {/* Exit Intent Upsell Popup */}
      {showExitPopup && <ExitPopup onClose={() => setShowExitPopup(false)} onAccept={() => {
      setShowExitPopup(false);
      window.open(ALL_BUMPS_URL, '_blank', 'noopener,noreferrer');
    }} onDecline={() => {
      setShowExitPopup(false);
      window.open(BASE_CART_URL, '_blank', 'noopener,noreferrer');
    }} data-id="element-504" />}
    </div>;
}